import { useState } from 'react';
import cn from 'classnames';
import { TextButton, Typography } from 'seagull';
import { routes } from "../../../../constants";
import { useEventReporter, useIoContext, useStoreActions } from "../../../../contexts";
import { useOnMount } from "../../../../hooks/useOnMount";
import { useUtmsParams } from "../../../../hooks/useUtmsParams";
import { handlePromise } from "../../../../infra/utils";
import { ErrorMessage, FormContainer, FormDescription, FormFields, VerticalFormFields, FormTitle, LinkButton } from "../../components/FormComponents";
import sharedStyles from "../LoginScreen.module.scss";
import styles from "./OTP.module.scss";
import OTPField from "./OTPField";
import { notices } from "./OTPField.utils";

// following the best practices as describe here: https://web.dev/sms-otp-form/
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function OTPStep(_ref) {
  let {
    titleMP,
    subtitleMP,
    email,
    hasPassword,
    simplified
  } = _ref;
  const [isLoading, setIsLoading] = useState(false);
  const {
    reportFormSubmission,
    reportToHeap
  } = useEventReporter();
  const {
    locationReload
  } = useIoContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const getUtms = useUtmsParams();
  const [otpSessionStatus, setOtpSessionStatus] = useState();
  const notice = notices[otpSessionStatus !== null && otpSessionStatus !== void 0 ? otpSessionStatus : ''];
  const inlineError = (notice === null || notice === void 0 ? void 0 : notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
  const floatingError = (notice === null || notice === void 0 ? void 0 : notice.isError) && !(notice !== null && notice !== void 0 && notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
  const floatingMessage = !(notice !== null && notice !== void 0 && notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.text);
  useOnMount(() => {
    api.auth.requestNewOTP(email);
  });
  async function validateExistingOTP(otp) {
    setIsLoading(true);
    const result = await handlePromise(api.auth.validateOTP(email, otp, getUtms()));
    if (!result.success) {
      setOtpSessionStatus(getErrorCause(result.error));
      setIsLoading(false);
    } else {
      reportFormSubmission('general-logged-in-successfully', null);
      reportToHeap('Login Screen', 'click', 'signed-in-with-six-digits-code', {
        email
      });
      logBI(16001, {
        email
      });
      locationReload();
    }
  }
  async function getNewOTP() {
    logBI(16000, {
      email
    });
    reportToHeap('Login Screen', 'click', 'clicked-send-me-six-digits-code', {
      email
    });
    const result = await handlePromise(api.auth.requestNewOTP(email));
    if (!result.success) {
      setOtpSessionStatus(getErrorCause(result.error));
    } else {
      setOtpSessionStatus('otpRequestOK');
    }
  }
  function getErrorCause(error) {
    if (error.response) {
      if (error.response.status === 400) {
        return 'wrongCode';
      } else if (error.response.status === 429) {
        return 'tooManyRequests';
      }
    }
    return 'unknown';
  }
  return /*#__PURE__*/_jsxs(FormContainer, {
    onSubmit: e => e.preventDefault(),
    "data-testid": "login-form",
    children: [simplified ? titleMP : /*#__PURE__*/_jsx(FormTitle, {
      children: "Check your email"
    }), simplified && subtitleMP, /*#__PURE__*/_jsx("div", {
      style: {
        textAlign: 'center'
      },
      children: !simplified && /*#__PURE__*/_jsxs(FormDescription, {
        children: ["If there's an account associated with", ' ', /*#__PURE__*/_jsx(Typography, {
          as: "span",
          weight: "bold",
          children: email
        }), ", you'll receive an email with a six digit code to login."]
      })
    }), /*#__PURE__*/_jsx(FormFields, {
      children: /*#__PURE__*/_jsxs(VerticalFormFields, {
        children: [/*#__PURE__*/_jsxs("div", {
          children: [simplified && /*#__PURE__*/_jsxs(Typography, {
            className: cn(sharedStyles.withBottomMargin, sharedStyles.centeredText),
            children: ["We emailed your code to:", ' ', /*#__PURE__*/_jsx(Typography, {
              as: "span",
              weight: "bold",
              children: email
            })]
          }), /*#__PURE__*/_jsx(OTPField, {
            isLoading: isLoading,
            error: Boolean(inlineError),
            helperText: inlineError,
            autoFocus: true,
            onChange: (code, allDigitsEntered) => {
              if (!allDigitsEntered) return;
              validateExistingOTP(code);
            }
          })]
        }), floatingMessage && /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: "success",
          "data-testid": "info-message",
          children: floatingMessage
        }), floatingError && /*#__PURE__*/_jsx(ErrorMessage, {
          "data-testid": "err-message",
          "data-err": notice === null || notice === void 0 ? void 0 : notice.name,
          children: floatingError
        })]
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: sharedStyles.footer,
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.requestCode,
        children: [simplified && /*#__PURE__*/_jsxs(Typography, {
          as: "p",
          variant: "paragraph2",
          children: ["Haven\u2019t received it?", ' ', /*#__PURE__*/_jsx(TextButton, {
            "data-testid": "get-new-code-button",
            underline: "none",
            variant: "paragraph2",
            onClick: async e => {
              e.preventDefault();
              getNewOTP();
            },
            children: "Get a new code"
          })]
        }), /*#__PURE__*/_jsxs(Typography, {
          as: "p",
          variant: "paragraph2",
          skin: "secondary",
          className: sharedStyles.centeredText,
          children: [simplified ? 'Check your spam/updates folder, or contact your service provider to verify the email used.' : 'Check your spam/updates folder or', ' ', !simplified && /*#__PURE__*/_jsx(TextButton, {
            "data-testid": "get-new-code-button",
            variant: "paragraph2",
            underline: "none",
            onClick: async e => {
              e.preventDefault();
              getNewOTP();
            },
            children: "get a new code"
          })]
        })]
      }), hasPassword || simplified || /*#__PURE__*/_jsxs(Typography, {
        as: "p",
        variant: "paragraph2",
        skin: "secondary",
        "data-testid": "set-a-password-button",
        children: ["Prefer to set a password?", ' ', /*#__PURE__*/_jsx(LinkButton, {
          toInternalLink: routes.resetPassword,
          "data-testid": "set-a-password",
          children: "Do it here"
        })]
      }), /*#__PURE__*/_jsx(LinkButton, {
        toInternalLink: routes.login,
        "data-testid": "back-link",
        className: sharedStyles.backButton,
        children: simplified ? 'Try another email' : 'Back'
      })]
    })]
  });
}